import * as React from "react";

import { Card, Grid, Label, Button, Checkbox } from "semantic-ui-react";
import { sortBy } from "lodash";

import { CaseConversationTaskModel, isCaseConversationTask } from "@teamprevent/common/messaging/models";
import { MessagingReplyModalButton } from "@teamprevent/common/messaging/components/MessagingReplyModalButton";
import { medicalTypes } from "@teamprevent/common/healthSurveillance/models";
import { TaskModel } from "@teamprevent/common/task/taskActions/models";

import { ImmunisationTaskModel, isHealthSurveillanceTask, isImmunisationTask } from "../../task/model";
import { taskActions } from "../../task/taskActions";
import { getPpqOutcome } from "../../triage/models";

import { Link } from "redux-little-router";

interface Props {
    task: (TaskModel | ImmunisationTaskModel | CaseConversationTaskModel);
    selected: boolean;
    userId: string;
    onSelectionChanged(taskId: string, selected: boolean): void;
}

export const ActionableTaskItem: React.FunctionComponent<Props> = (props: Props) => {

    const buildCaseLink = () => `/customers/${props.task.customer.id}/employeeRecords/${props.task.employeeRecord.id}/ohCases/${props.task.caseId}`;
    const buildCaseConversationLink = (task: (TaskModel | ImmunisationTaskModel | CaseConversationTaskModel)) => {
        return `/customers/${props.task.customer.id}/employeeRecords/${props.task.employeeRecord.id}/ohCases/` +
            `${props.task.caseId}/conversation?subject=${(task as CaseConversationTaskModel).caseSubject}`;
    };

    const { task, selected, onSelectionChanged } = props;
    const onSelectedChanged = React.useCallback(() => {
        onSelectionChanged(task.id, !selected);
    }, [onSelectionChanged, selected, task.id]);

    const caseMessaging = isCaseConversationTask(props.task);

    const ppqOutcome = getPpqOutcome(props.task?.ppqOutcome?.id);

    return (
        <Card fluid>
            <Card.Content>
                <Card.Header>{props.task.description}</Card.Header>
                <Grid stackable>
                    {isImmunisationTask(props.task) && props.task.immunisationTypeTasks?.length &&
                        <Grid.Row>
                            <Grid.Column>
                                {props.task.immunisationTypeTasks?.map(immType =>
                                    <div key={immType.id}>{immType.name}</div>
                                )}
                            </Grid.Column>
                        </Grid.Row>
                    }
                    {isHealthSurveillanceTask(props.task) && props.task.medicalTypes?.length &&
                        <>
                            <Grid.Row>
                                <Grid.Column>
                                    <strong>Medicals:</strong>
                                    <div>
                                        {sortBy(props.task.medicalTypes, [mt => mt.toLowerCase()])?.map(medicalType =>
                                            <span key={medicalType}>&#8226; {medicalTypes.find(mt => mt.id === medicalType)?.name} </span>
                                        )}
                                    </div>
                                </Grid.Column>
                            </Grid.Row>
                            {ppqOutcome &&
                                <Grid.Row>
                                    <Grid.Column><div><strong>PPQ Outcome:</strong> {ppqOutcome?.text}</div></Grid.Column>
                                </Grid.Row>
                            }
                        </>
                    }
                    <Grid.Row stretched columns={4}>
                        <Grid.Column><strong>Created:</strong> {props.task.dateCreated ? props.task.dateCreated.format("L") : ""}</Grid.Column>
                        <Grid.Column><strong>Due:</strong> {props.task.dueDate ? props.task.dueDate.format("L") : ""}</Grid.Column>
                        <Grid.Column><strong>Customer:</strong> {props.task.customer.name}</Grid.Column>
                        <Grid.Column><strong>Employee:</strong> {props.task.employeeRecord.name}</Grid.Column>
                    </Grid.Row>
                </Grid>
            </Card.Content>
            <Card.Content extra>
                <Checkbox checked={props.selected} onChange={onSelectedChanged} />
                {props.task.assignedUsers && props.task.assignedUsers.length ?
                    <>
                        Assigned to {props.task.assignedUsers.map(u => (
                            <Label key={u.id} size="tiny" color={u.id === props.userId ? "green" : "grey"} content={u.name}
                            />))}
                    </> :
                    <>
                        <Label size="tiny" color="red" content="No-one assigned" />
                    </>}
                {props.task.actions && props.task.actions.filter(a => taskActions[a])
                    .map(a => (
                        <span key={a}>
                            {taskActions[a](props.task, props.task.customer.id)}
                        </span>))}
                <Link href={buildCaseLink()} target="_blank">
                    <Button floated="right" content="View Case" />
                </Link>
                {caseMessaging &&
                    <>
                        <MessagingReplyModalButton
                            buttonText={"Reply"}
                            subject={(props.task as CaseConversationTaskModel).caseSubject}
                            caseId={props.task.caseId}
                            showReply={true}
                            customerId={props.task.customer.id}
                            employeeName={props.task.employeeRecord.name}
                        />
                        <Link href={buildCaseConversationLink(props.task)} target="_blank">
                            <Button floated="right" content="View Conversation" />
                        </Link>
                    </>
                }
            </Card.Content>
        </Card>
    );
};
